import { useRadio, useRadioGroup } from '@react-aria/radio';
import { RadioGroupState, useRadioGroupState } from '@react-stately/radio';
import omit from 'lodash/omit';
import React, { FocusEvent, HTMLProps, MouseEventHandler, PropsWithChildren, ReactElement } from 'react';
import { useTheme } from 'styled-components';

import { useAccessibleOnChange } from '../../utils/useAccessibleOnChange';
import { CalmTheme } from '../ThemeProvider';
import { CardsContainer, Container, DisplayDiv, Indicator, StyledLabel, Wrapper } from './styles';
import { RadioGroupProps } from './types';

const RadioContext = React.createContext<RadioGroupState>({
	name: '',
	isDisabled: false,
	isReadOnly: false,
	selectedValue: null,
	setSelectedValue: () => {},
	lastFocusedValue: null,
	setLastFocusedValue: () => {},
	validationState: 'invalid',
} as unknown as RadioGroupState);

type RadioButtonProps = Omit<
	PropsWithChildren<HTMLProps<HTMLInputElement>>,
	'value' | 'onFocus' | 'onBlur'
> & {
	onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	value: string;
	asCard: boolean;
	fullWidth: boolean;
	display: 'block' | 'flex';
	noMarginBottom?: boolean; // removes margin from the bottom row of the radio group, preserves space between wrapped rows
};

function RadioButton(props: RadioButtonProps): ReactElement {
	const { children, asCard, fullWidth, display, noMarginBottom, ...htmlProps } = props;
	const state = React.useContext(RadioContext);
	const ref = React.useRef(null);
	const massagedProps = {
		isDisabled: htmlProps.disabled,
		children,
		...htmlProps,
		onClick: htmlProps.onClick as MouseEventHandler | undefined,
		onFocus: htmlProps.onFocus as ((e: React.FocusEvent<Element, Element>) => void) | undefined,
		onBlur: htmlProps.onBlur as ((e: React.FocusEvent<Element, Element>) => void) | undefined,
	};
	const { inputProps } = useRadio(massagedProps, state, ref);
	return (
		<Container
			$asCard={asCard}
			$fullWidth={fullWidth}
			$display={display}
			disabled={inputProps.disabled}
			checked={inputProps.checked}
			$noMarginBottom={noMarginBottom}
		>
			<input type="radio" {...htmlProps} {...inputProps} />
			<Indicator />
			{children}
		</Container>
	);
}

const RadioGroup = (props: RadioGroupProps): ReactElement => {
	const {
		options,
		value,
		label,
		LabelComponent,
		onChange,
		onFocus,
		onBlur,
		disabled,
		required,
		readOnly,
		name: htmlName,
		asCards = false,
		fullWidth = false,
		noMarginBottom = false,
		display = 'block',
	} = props;
	const wrappedOnChange = useAccessibleOnChange(onChange, htmlName, 'radio');
	const massagedProps = {
		...props,
		onChange: wrappedOnChange,
		onFocus: onFocus as ((e: FocusEvent<Element, Element>) => void) | undefined,
		onBlur: onBlur as ((e: FocusEvent<Element, Element>) => void) | undefined,
	};
	const state = useRadioGroupState(massagedProps);
	const { radioGroupProps, labelProps } = useRadioGroup(massagedProps, state);
	const theme = useTheme() as typeof CalmTheme;

	const buttons = (
		<Wrapper {...radioGroupProps} $fullWidth={fullWidth}>
			{LabelComponent ? (
				<LabelComponent {...labelProps}>{label}</LabelComponent>
			) : label ? (
				// We need to omit color because labelProps is React.HTMLAttributes<HTMLElement> which could include color
				// but in practice a) it'll never be there, and b) it'd be the wrong type (a hexcode, not a palette key)
				<StyledLabel {...omit(labelProps, 'color')} {...theme.radioGroup?.labelProps}>
					{label}
				</StyledLabel>
			) : null}
			<RadioContext.Provider value={state}>
				<DisplayDiv $display={display} $noMarginBottom={noMarginBottom}>
					{options.map(option => (
						<RadioButton
							key={option.value}
							value={option.value}
							id={option.id}
							checked={option.value === value}
							name={htmlName}
							disabled={disabled}
							required={required}
							readOnly={readOnly}
							asCard={asCards}
							fullWidth={fullWidth}
							display={display}
							noMarginBottom={noMarginBottom}
						>
							{option.label}
						</RadioButton>
					))}
				</DisplayDiv>
			</RadioContext.Provider>
		</Wrapper>
	);
	return asCards ? <CardsContainer>{buttons}</CardsContainer> : buttons;
};

export default RadioGroup;
